import { useState, useEffect } from "react";
import { CustomerHeader } from "@/components/CustomerHeader";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Gift, Clock, CheckCircle, Users, Loader2, AlertCircle, Heart, CreditCard, Sparkles, ExternalLink, Mail, ArrowLeft, Phone, Shield, Eye, Bell, Wallet, Package, User, Store, MessageSquare, ShoppingBag, Share2, Twitter, Facebook, Send, CalendarPlus, Plus, Copy } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link, useSearchParams } from "react-router-dom";
import { FAQChatBot } from "@/components/FAQChatBot";
import { CustomerEngagementSection } from "@/components/CustomerEngagementSection";
import { RecommendStoreDialog } from "@/components/RecommendStoreDialog";
import { RecommendFriendDialog } from "@/components/RecommendFriendDialog";
import { CustomerFeedbackDialog } from "@/components/CustomerFeedbackDialog";
import { CustomerImportantDates } from "@/components/CustomerImportantDates";
import { GamificationBadges } from "@/components/GamificationBadges";
import { SocialShareSection } from "@/components/SocialShareSection";
import { AddGroupPurchaseDialog } from "@/components/AddGroupPurchaseDialog";
import { AddGroupPaymentDialog } from "@/components/AddGroupPaymentDialog";
import { AddGroupGiftDialog } from "@/components/AddGroupGiftDialog";
import { CustomerFAQ } from "@/components/CustomerFAQ";
import { supabase } from "@/integrations/supabase/client";
import { getSupabaseFunctionErrorMessage } from "@/lib/supabaseFunctionErrors";
import { buildPublicAppUrl } from "@/lib/appConfig";
import { isRunningInsideShopifyAdmin, openShopifyTopLevel } from "@/lib/shopifyEmbedded";
import { formatCurrency, type Currency } from "@/lib/currency";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";

interface GiftParticipation {
  id: string;
  request_id: string;
  name: string;
  email: string;
  has_paid: boolean;
  is_organizer: boolean;
  request: {
    id: string;
    created_at: string;
    product_name: string;
    product_image_url: string | null;
    product_description: string | null;
    status: string;
    deadline: string;
    total_price: number;
    split_amount: number;
    customer_name: string;
    is_gift: boolean;
    merchant_id: string | null;
    shopify_order_id: string | null;
    fulfillment_status: string | null;
    tracking_url: string | null;
    tracking_number: string | null;
    carrier: string | null;
    store_name: string | null;
    store_domain: string | null;
    currency: string | null;
    participant_count: number;
    paid_count: number;
    participants: Array<{
      id: string;
      name: string;
      email: string;
      has_paid: boolean;
      is_organizer: boolean | null;
    }>;
  };
}

// Gift Events from gift_reminders table
interface GiftEvent {
  id: string;
  event_name: string;
  recipient_name: string | null;
  event_date: string;
  occasion: string | null;
  is_recurring: boolean;
  role: 'organizer' | 'participant';
}

// Group Purchases/Payments from group_purchases table
interface GroupActivity {
  id: string;
  title: string;
  organizer_name: string | null;
  target_date: string | null;
  status: string;
  payment_type: string | null;
  collected_amount: number | null;
  target_amount: number | null;
  role: 'organizer' | 'admin' | 'participant';
}

type Step = "email" | "otp" | "gifts";
type VerifyMethod = "email" | "phone";

const SESSION_KEY = "shakatah_customer_session";

interface StoredSession {
  email?: string;
  phone?: string;
  method: VerifyMethod;
}

const COUNTRY_CODES = [
  { code: "+91", country: "IN", flag: "🇮🇳" },
  { code: "+1", country: "US", flag: "🇺🇸" },
  { code: "+44", country: "UK", flag: "🇬🇧" },
  { code: "+971", country: "AE", flag: "🇦🇪" },
  { code: "+65", country: "SG", flag: "🇸🇬" },
  { code: "+61", country: "AU", flag: "🇦🇺" },
  { code: "+49", country: "DE", flag: "🇩🇪" },
];

const BENEFITS = [
  {
    icon: <Eye className="w-5 h-5" />,
    title: "All Purchases in One Place",
    description: "See every group purchase you're part of"
  },
  {
    icon: <Bell className="w-5 h-5" />,
    title: "Payment Reminders",
    description: "Never miss a deadline with timely notifications"
  },
  {
    icon: <Shield className="w-5 h-5" />,
    title: "Secure & Private",
    description: "Your data is protected with verification"
  },
  {
    icon: <Wallet className="w-5 h-5" />,
    title: "Easy Payments",
    description: "Pay your share in just a few clicks"
  },
];

const MyGifts = () => {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [step, setStep] = useState<Step>("email");
  const [verifyMethod, setVerifyMethod] = useState<VerifyMethod>("email");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [otp, setOtp] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gifts, setGifts] = useState<GiftParticipation[]>([]);
  const [verifiedEmail, setVerifiedEmail] = useState<string | null>(null);
  const [verifiedPhone, setVerifiedPhone] = useState<string | null>(null);
  const [linkedPhone, setLinkedPhone] = useState<string | null>(null);
  const [openAddDateDialog, setOpenAddDateDialog] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [addPhoneDialogOpen, setAddPhoneDialogOpen] = useState(false);
  const [newPhone, setNewPhone] = useState("");
  const [newPhoneCountryCode, setNewPhoneCountryCode] = useState("+91");
  const [savingPhone, setSavingPhone] = useState(false);
  const [openGroupPurchaseDialog, setOpenGroupPurchaseDialog] = useState(false);
  const [openGroupPaymentDialog, setOpenGroupPaymentDialog] = useState(false);
  const [openGroupGiftDialog, setOpenGroupGiftDialog] = useState(false);
  const [openEventTypePicker, setOpenEventTypePicker] = useState(false);
  const [calendarSelectedDate, setCalendarSelectedDate] = useState<Date | undefined>(undefined);
  const [resendingParticipantId, setResendingParticipantId] = useState<string | null>(null);
  
  // New state for categorized activities
  const [giftEvents, setGiftEvents] = useState<GiftEvent[]>([]);
  const [groupPurchases, setGroupPurchases] = useState<GroupActivity[]>([]);
  const [groupPayments, setGroupPayments] = useState<GroupActivity[]>([]);

  // Handle addReminder query param
  useEffect(() => {
    if (searchParams.get('addReminder') === 'true' && step === 'gifts') {
      setOpenAddDateDialog(true);
      // Clear the param so it doesn't re-open on navigation
      searchParams.delete('addReminder');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, step, setSearchParams]);

  // Check for existing valid session on mount - persists until tab is closed
  useEffect(() => {
    if (isRunningInsideShopifyAdmin()) {
      const search = window.location.search || "";
      const hash = window.location.hash || "";
      openShopifyTopLevel(buildPublicAppUrl(`/my-purchases${search}${hash}`));
      return;
    }

    const storedSession = sessionStorage.getItem(SESSION_KEY);
    if (storedSession) {
      try {
        const session: StoredSession = JSON.parse(storedSession);
        if (session.email || session.phone) {
          if (session.email) setVerifiedEmail(session.email);
          if (session.phone) setVerifiedPhone(session.phone);
          setStep("gifts");
          fetchGifts(session.email, session.phone);
          fetchCategorizedActivities(session.email, session.phone);
        } else {
          sessionStorage.removeItem(SESSION_KEY);
        }
      } catch {
        sessionStorage.removeItem(SESSION_KEY);
      }
    }
    setSessionChecked(true);
  }, []);

  // Handle browser back/forward - re-check session state
  useEffect(() => {
    const handlePopState = () => {
      const storedSession = sessionStorage.getItem(SESSION_KEY);
      if (storedSession) {
        try {
          const session: StoredSession = JSON.parse(storedSession);
          if (session.email || session.phone) {
            if (session.email) setVerifiedEmail(session.email);
            if (session.phone) setVerifiedPhone(session.phone);
            setStep("gifts");
          }
        } catch {
          // Session invalid, will show login
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const handleSendOtp = async (e?: React.FormEvent) => {
    e?.preventDefault();
    
    const identifier = verifyMethod === "email" ? email.trim() : `${countryCode}${phone.replace(/^0+/, '')}`;
    if (!identifier) return;

    setIsSendingOtp(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke("send-gift-verification-otp", {
        body: { 
          email: verifyMethod === "email" ? email.trim() : undefined,
          phone: verifyMethod === "phone" ? `${countryCode}${phone.replace(/^0+/, '')}` : undefined,
          method: verifyMethod
        },
      });

      if (fnError) throw fnError;

      toast({
        title: "Code sent!",
        description: verifyMethod === "email" 
          ? "Check your email for the 6-digit verification code."
          : "Check your phone for the 6-digit verification code.",
      });
      setStep("otp");
    } catch (err: any) {
      console.error("Error sending OTP:", err);
      const errorMessage = await getSupabaseFunctionErrorMessage(
        err,
        "Failed to send verification code",
      );
      setError(errorMessage);
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) return;

    setIsLoading(true);
    setError(null);

    const identifier = verifyMethod === "email" ? email.trim() : `${countryCode}${phone.replace(/^0+/, '')}`;

    try {
      // Build the optional phone number if provided
      const optionalPhoneNumber = phone.trim() ? `${countryCode}${phone.replace(/^0+/, '')}` : undefined;
      
      const { data, error: fnError } = await supabase.functions.invoke("verify-gift-otp", {
        body: { 
          email: verifyMethod === "email" ? email.trim() : undefined,
          phone: verifyMethod === "phone" ? `${countryCode}${phone.replace(/^0+/, '')}` : undefined,
          code: otp,
          method: verifyMethod,
          optionalPhone: verifyMethod === "email" ? optionalPhoneNumber : undefined
        },
      });

      if (fnError) throw fnError;

      if (data?.error) {
        setError(data.error);
        setIsLoading(false);
        return;
      }

      // Store session - persists until tab is closed
      const session: StoredSession = {
        email: data.email,
        phone: data.phone,
        method: verifyMethod,
      };
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));

      if (data.email) setVerifiedEmail(data.email);
      if (data.phone) setVerifiedPhone(data.phone);
      setStep("gifts");
      await fetchGifts(data.email, data.phone);
      await fetchCategorizedActivities(data.email, data.phone);
    } catch (err: any) {
      console.error("Error verifying OTP:", err);
      const errorMessage = await getSupabaseFunctionErrorMessage(
        err,
        "Failed to verify code",
      );
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchGifts = async (emailToFetch?: string, phoneToFetch?: string) => {
    setIsLoading(true);
    setError(null);

    try {
      let participants;
      
      if (emailToFetch) {
        const normalizedEmail = emailToFetch.trim().toLowerCase();
        const { data, error } = await supabase
          .from("split_payment_participants")
          .select(`id, request_id, name, email, has_paid, is_organizer`)
          .ilike("email", normalizedEmail);
        if (error) throw error;
        participants = data || [];

        const { data: organizerRequests, error: organizerRequestsError } = await supabase
          .from("split_payment_requests")
          .select("id")
          .ilike("customer_email", normalizedEmail);

        if (organizerRequestsError) throw organizerRequestsError;

        const organizerRequestIds = organizerRequests?.map((request) => request.id) || [];
        if (organizerRequestIds.length > 0) {
          const { data: organizerParticipants, error: organizerParticipantsError } = await supabase
            .from("split_payment_participants")
            .select(`id, request_id, name, email, has_paid, is_organizer`)
            .in("request_id", organizerRequestIds)
            .eq("is_organizer", true);

          if (organizerParticipantsError) throw organizerParticipantsError;

          const participantMap = new Map((participants || []).map((participant) => [participant.id, participant]));
          organizerParticipants?.forEach((participant) => {
            participantMap.set(participant.id, participant);
          });
          participants = Array.from(participantMap.values());
        }
      } else if (phoneToFetch) {
        // For phone, we need to check with flexible matching
        const { data, error } = await supabase
          .from("split_payment_participants")
          .select(`id, request_id, name, email, has_paid, is_organizer, mobile`);
        if (error) throw error;
        
        const normalizedPhone = phoneToFetch.replace(/\s+/g, '');
        participants = data?.filter(p => {
          const participantPhone = p.mobile?.replace(/\s+/g, '');
          return participantPhone === normalizedPhone || 
                 participantPhone?.endsWith(normalizedPhone.slice(-10)) ||
                 normalizedPhone.endsWith(participantPhone?.slice(-10) || '');
        });
      }

      if (!participants || participants.length === 0) {
        setGifts([]);
        setIsLoading(false);
        return;
      }

      // Get the request details for each participation
      const requestIds = [...new Set(participants.map(p => p.request_id))];
      const { data: requests, error: requestError } = await supabase
        .from("split_payment_requests")
        .select(`
          id,
          created_at,
          product_name,
          product_image_url,
          product_description,
          status,
          deadline,
          total_price,
          split_amount,
          customer_name,
          is_gift,
          merchant_id,
          shopify_order_id,
          fulfillment_status,
          tracking_url,
          tracking_number,
          carrier
        `)
        .in("id", requestIds);

      if (requestError) throw requestError;

      const { data: requestParticipants, error: requestParticipantsError } = await supabase
        .from("split_payment_participants")
        .select("id, request_id, name, email, has_paid, is_organizer")
        .in("request_id", requestIds);

      if (requestParticipantsError) throw requestParticipantsError;

      const requestStats = new Map<string, { participant_count: number; paid_count: number }>();
      const requestParticipantMap = new Map<string, GiftParticipation["request"]["participants"]>();
      requestParticipants?.forEach((participant) => {
        const current = requestStats.get(participant.request_id) || { participant_count: 0, paid_count: 0 };
        current.participant_count += 1;
        if (participant.has_paid) {
          current.paid_count += 1;
        }
        requestStats.set(participant.request_id, current);

        const existing = requestParticipantMap.get(participant.request_id) || [];
        existing.push({
          id: participant.id,
          name: participant.name,
          email: participant.email,
          has_paid: participant.has_paid,
          is_organizer: participant.is_organizer,
        });
        requestParticipantMap.set(participant.request_id, existing);
      });

      const merchantIds = [...new Set((requests || []).map((request) => request.merchant_id).filter(Boolean))] as string[];
      const merchantEntries = await Promise.all(
        merchantIds.map(async (merchantId) => {
          const { data: merchantData, error: merchantError } = await supabase.rpc("get_split_merchant_info", {
            request_merchant_id: merchantId,
          });

          if (merchantError) {
            console.error("Error fetching merchant info for request list:", merchantError);
            return [merchantId, null] as const;
          }

          return [merchantId, merchantData?.[0] || null] as const;
        })
      );
      const merchantMap = new Map(merchantEntries);
      const requestMap = new Map((requests || []).map((request) => [request.id, request]));

      // Combine participant data with request data
      const giftsWithDetails = participants.map(participant => {
        const request = requestMap.get(participant.request_id);
        const merchant = request?.merchant_id ? merchantMap.get(request.merchant_id) : null;
        const stats = requestStats.get(participant.request_id) || { participant_count: 0, paid_count: 0 };

        return {
          ...participant,
          request: request
            ? {
                ...request,
                store_name: merchant?.store_name || null,
                store_domain: merchant?.shopify_store_domain || null,
                currency: merchant?.currency || null,
                participant_count: stats.participant_count,
                paid_count: stats.paid_count,
                participants: requestParticipantMap.get(request.id) || [],
              }
            : null,
        };
      }).filter(g => g.request);

      // Sort: pending first, then by deadline
      giftsWithDetails.sort((a, b) => {
        if (a.request.status === "pending" && b.request.status !== "pending") return -1;
        if (a.request.status !== "pending" && b.request.status === "pending") return 1;
        return new Date(b.request.created_at).getTime() - new Date(a.request.created_at).getTime();
      });

      setGifts(giftsWithDetails);
    } catch (err) {
      console.error("Error fetching gifts:", err);
      setError("Failed to fetch your gifts. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch categorized activities (gift events, group purchases, group payments)
  const fetchCategorizedActivities = async (emailToFetch?: string, phoneToFetch?: string) => {
    if (!emailToFetch && !phoneToFetch) return;
    
    try {
      const emailLower = emailToFetch?.toLowerCase();
      
      // Fetch Gift Events from gift_reminders
      if (emailLower) {
        // Get events where user is organizer
        const { data: organizerEvents } = await supabase
          .from("gift_reminders")
          .select("id, event_name, recipient_name, event_date, occasion, is_recurring")
          .eq("customer_email", emailLower)
          .order("event_date", { ascending: false })
          .limit(5);
        
        // Get events where user is invited
        const { data: invitedEvents } = await supabase
          .from("gift_reminders")
          .select("id, event_name, recipient_name, event_date, occasion, is_recurring")
          .or(`reminder_email.eq.${emailLower},invited_friend_email.eq.${emailLower}`)
          .order("event_date", { ascending: false })
          .limit(5);
        
        // Combine and deduplicate
        const allGiftEvents: GiftEvent[] = [];
        organizerEvents?.forEach(e => {
          if (!allGiftEvents.find(x => x.id === e.id)) {
            allGiftEvents.push({ ...e, role: 'organizer' });
          }
        });
        invitedEvents?.forEach(e => {
          if (!allGiftEvents.find(x => x.id === e.id)) {
            allGiftEvents.push({ ...e, role: 'participant' });
          }
        });
        
        setGiftEvents(allGiftEvents.slice(0, 5));
      }
      
      // Fetch Group Purchases and Payments from group_purchases
      if (emailLower) {
        // Get activities where user is organizer
        const { data: organizerActivities } = await supabase
          .from("group_purchases")
          .select("id, title, organizer_name, target_date, status, payment_type, collected_amount, target_amount")
          .eq("organizer_email", emailLower)
          .eq("status", "active")
          .order("created_at", { ascending: false })
          .limit(10);
        
        // Get activities where user is participant
        const { data: participantData } = await supabase
          .from("group_purchase_participants")
          .select("purchase_id, role")
          .eq("email", emailLower)
          .eq("status", "active");
        
        let participantActivities: any[] = [];
        if (participantData && participantData.length > 0) {
          const purchaseIds = participantData.map(p => p.purchase_id);
          const { data: activities } = await supabase
            .from("group_purchases")
            .select("id, title, organizer_name, target_date, status, payment_type, collected_amount, target_amount")
            .in("id", purchaseIds)
            .eq("status", "active");
          participantActivities = activities || [];
        }
        
        // Combine and categorize
        const allActivities: GroupActivity[] = [];
        const roleMap = new Map(participantData?.map(p => [p.purchase_id, p.role]) || []);
        
        organizerActivities?.forEach(a => {
          if (!allActivities.find(x => x.id === a.id)) {
            allActivities.push({ ...a, role: 'organizer' });
          }
        });
        participantActivities.forEach(a => {
          if (!allActivities.find(x => x.id === a.id)) {
            const role = roleMap.get(a.id) === 'admin' ? 'admin' : 'participant';
            allActivities.push({ ...a, role });
          }
        });
        
        // Split into purchases vs payments using the explicit category field
        const purchases = allActivities.filter(a => (a as any).category !== 'payment');
        const payments = allActivities.filter(a => (a as any).category === 'payment');
        
        setGroupPurchases(purchases.slice(0, 5));
        setGroupPayments(payments.slice(0, 5));
      }
    } catch (error) {
      console.error("Error fetching categorized activities:", error);
    }
  };

  const handleBack = () => {
    setStep("email");
    setOtp("");
    setError(null);
  };

  const handleLogout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setVerifiedEmail(null);
    setVerifiedPhone(null);
    setLinkedPhone(null);
    setGifts([]);
    setStep("email");
    setEmail("");
    setPhone("");
    setOtp("");
  };

  // Fetch linked phone when verified
  useEffect(() => {
    if (verifiedEmail) {
      fetchLinkedPhone();
    }
  }, [verifiedEmail]);

  const fetchLinkedPhone = async () => {
    if (!verifiedEmail) return;
    try {
      const { data } = await supabase
        .from("customer_profiles")
        .select("phone")
        .eq("email", verifiedEmail.toLowerCase())
        .single();
      if (data?.phone) {
        setLinkedPhone(data.phone);
      }
    } catch {
      // Profile might not exist
    }
  };

  const handleAddPhone = async () => {
    if (!newPhone.trim()) {
      toast({ title: "Please enter a phone number", variant: "destructive" });
      return;
    }

    setSavingPhone(true);
    try {
      const fullPhone = `${newPhoneCountryCode}${newPhone.replace(/^0+/, '')}`;
      
      // Check if profile exists
      const { data: existingProfile } = await supabase
        .from("customer_profiles")
        .select("*")
        .eq("email", verifiedEmail?.toLowerCase())
        .single();

      if (existingProfile) {
        const { error } = await supabase
          .from("customer_profiles")
          .update({ phone: fullPhone })
          .eq("id", existingProfile.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("customer_profiles")
          .insert({ email: verifiedEmail?.toLowerCase(), phone: fullPhone });
        if (error) throw error;
      }

      toast({ title: "Phone number added!", description: "You can now log in with either email or phone." });
      setLinkedPhone(fullPhone);
      setAddPhoneDialogOpen(false);
      setNewPhone("");
    } catch (error: any) {
      console.error("Error saving phone:", error);
      if (error.message?.includes("duplicate")) {
        toast({ title: "This phone is already linked to another account", variant: "destructive" });
      } else {
        toast({ title: "Failed to save phone number", variant: "destructive" });
      }
    } finally {
      setSavingPhone(false);
    }
  };

  const getStatusBadge = (status: string, hasPaid: boolean) => {
    if (status === "completed") {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">Completed</span>;
    }
    if (status === "expired" || status === "cancelled") {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-muted text-muted-foreground">Expired</span>;
    }
    if (hasPaid) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-customer/10 text-customer">You've Paid</span>;
    }
    return <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">Payment Pending</span>;
  };

  const getFulfillmentLabel = (gift: GiftParticipation) => {
    if (gift.request.tracking_number) {
      return `Tracking ${gift.request.tracking_number}`;
    }

    if (gift.request.fulfillment_status === "fulfilled") {
      return "Fulfilled by store";
    }

    if (gift.request.status === "completed") {
      return "Paid and placed with store";
    }

    if (gift.has_paid) {
      return "Your share is confirmed";
    }

    return "Awaiting your contribution";
  };

  const buildParticipantPaymentLink = (requestId: string, participantEmail: string) =>
    buildPublicAppUrl(`/split/${requestId}?email=${encodeURIComponent(participantEmail)}`);

  const handleShareParticipantPaymentLink = async (requestId: string, participant: GiftParticipation["request"]["participants"][number]) => {
    const paymentLink = buildParticipantPaymentLink(requestId, participant.email);
    const shareText = `${participant.name || "Your friend"} can pay their Splitkart share here: ${paymentLink}`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: "Splitkart payment link",
          text: shareText,
          url: paymentLink,
        });
      } else {
        await navigator.clipboard.writeText(paymentLink);
        toast({ title: "Payment link copied", description: `Share it with ${participant.name || participant.email}.` });
      }
    } catch (error: any) {
      if (error?.name !== "AbortError") {
        console.error("Error sharing participant payment link:", error);
        toast({ title: "Could not share link", description: "The payment link was not shared.", variant: "destructive" });
      }
    }
  };

  const handleCopyParticipantPaymentLink = async (requestId: string, participantEmail: string) => {
    try {
      await navigator.clipboard.writeText(buildParticipantPaymentLink(requestId, participantEmail));
      toast({ title: "Payment link copied" });
    } catch (error) {
      console.error("Error copying participant payment link:", error);
      toast({ title: "Could not copy link", variant: "destructive" });
    }
  };

  const handleResendParticipantPaymentLink = async (requestId: string, participantId: string) => {
    setResendingParticipantId(participantId);
    try {
      const { data, error: fnError } = await supabase.functions.invoke("send-participant-checkouts", {
        body: {
          requestId,
          participantId,
          force: true,
        },
      });

      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);

      toast({
        title: "Payment email sent",
        description: data?.sent ? "The participant invitation was resent." : data?.message || "No pending participant email was sent.",
      });
    } catch (error: any) {
      console.error("Error resending participant payment email:", error);
      const errorMessage = await getSupabaseFunctionErrorMessage(error, "Failed to resend payment email");
      toast({ title: errorMessage, variant: "destructive" });
    } finally {
      setResendingParticipantId(null);
    }
  };

  const displayIdentifier = verifyMethod === "email" ? email : `${countryCode}${phone}`;

  // Show loading while checking session to prevent flash of login screen
  if (!sessionChecked) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <SEO 
          title="My Purchases - Shakatah"
          description="Track your group purchases and gift contributions."
          url="/my-purchases" 
        />
        <CustomerHeader showMyPurchasesLink={false} />
        <main className="flex-1 pt-14 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-customer" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO 
        title="My Purchases - Shakatah"
        description="Track your group purchases and gift contributions."
        url="/my-purchases" 
      />
      <CustomerHeader 
        userEmail={verifiedEmail} 
        linkedPhone={linkedPhone}
        onLogout={handleLogout}
        onAddPhone={() => setAddPhoneDialogOpen(true)}
        showMyPurchasesLink={false}
      />
      
      <main className="flex-1 pt-14 pb-16">
        {/* Hero Section - compact */}
        <section className="relative py-3 md:py-4 bg-gradient-to-br from-customer/10 via-background to-purple-100/30 dark:to-purple-950/20">
          <div className="container">
            {/* Content section - title moved to header */}
            <div className="max-w-2xl mx-auto text-center">
              
              {step === "email" && (
                <>
                  <p className="text-muted-foreground text-sm mb-4">
                    Verify your identity to see all the group purchases you're part of
                  </p>
                  
                  {/* Verification Form */}
                  <Card className="max-w-md mx-auto shadow-lg">
                    <CardContent className="p-6">
                      <form onSubmit={handleSendOtp} className="space-y-4">
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            type="email"
                            placeholder="Enter your email address..."
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="pl-10"
                            required
                          />
                        </div>

                        {/* Optional phone number */}
                        <div className="space-y-1">
                          <div className="flex gap-2">
                            <Select value={countryCode} onValueChange={setCountryCode}>
                              <SelectTrigger className="w-[100px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {COUNTRY_CODES.map((c) => (
                                  <SelectItem key={c.code} value={c.code}>
                                    {c.flag} {c.code}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <div className="relative flex-1">
                              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                              <Input
                                type="tel"
                                placeholder="Phone (optional)"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                                className="pl-10"
                              />
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground pl-1">Optional - helps us find all your purchases</p>
                        </div>
                        
                        <Button 
                          type="submit" 
                          disabled={isSendingOtp || !email.trim()} 
                          className="w-full bg-customer hover:bg-customer/90 text-customer-foreground"
                        >
                          {isSendingOtp ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : null}
                          Send Verification Code
                        </Button>
                        {error && (
                          <p className="text-sm text-destructive flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" />
                            {error}
                          </p>
                        )}
                      </form>
                    </CardContent>
                  </Card>
                </>
              )}

              {step === "otp" && (
                <>
                  <p className="text-muted-foreground mb-2">
                    Enter the 6-digit code sent to <span className="font-medium text-foreground">{displayIdentifier}</span>
                  </p>
                  
                  {/* OTP Form */}
                  <Card className="max-w-md mx-auto shadow-lg">
                    <CardContent className="p-6 space-y-4">
                      <div className="flex justify-center">
                        <InputOTP
                          maxLength={6}
                          value={otp}
                          onChange={(value) => setOtp(value)}
                        >
                          <InputOTPGroup>
                            <InputOTPSlot index={0} />
                            <InputOTPSlot index={1} />
                            <InputOTPSlot index={2} />
                            <InputOTPSlot index={3} />
                            <InputOTPSlot index={4} />
                            <InputOTPSlot index={5} />
                          </InputOTPGroup>
                        </InputOTP>
                      </div>
                      
                      {error && (
                        <p className="text-sm text-destructive flex items-center justify-center gap-2">
                          <AlertCircle className="w-4 h-4" />
                          {error}
                        </p>
                      )}
                      
                      <Button 
                        onClick={handleVerifyOtp} 
                        disabled={otp.length !== 6 || isLoading}
                        className="w-full bg-customer hover:bg-customer/90 text-customer-foreground"
                      >
                        {isLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : null}
                        Verify & Continue
                      </Button>
                      
                      <div className="flex justify-between text-sm">
                        <button 
                          onClick={handleBack}
                          className="text-muted-foreground hover:text-foreground flex items-center gap-1.5"
                        >
                          <ArrowLeft className="w-4 h-4" />
                          Back
                        </button>
                        <button 
                          onClick={handleSendOtp}
                          disabled={isSendingOtp}
                          className="text-customer hover:underline"
                        >
                          {isSendingOtp ? "Sending..." : "Resend Code"}
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>
          </div>
        </section>

        {/* Add Phone Dialog */}
        <Dialog open={addPhoneDialogOpen} onOpenChange={setAddPhoneDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add Phone Number</DialogTitle>
              <DialogDescription>
                Link your phone number to access your purchases with either email or phone.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Phone Number</Label>
                <div className="flex gap-2">
                  <Select value={newPhoneCountryCode} onValueChange={setNewPhoneCountryCode}>
                    <SelectTrigger className="w-[100px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRY_CODES.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.flag} {c.code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="tel"
                    placeholder="Phone number"
                    value={newPhone}
                    onChange={(e) => setNewPhone(e.target.value.replace(/\D/g, ''))}
                    className="flex-1"
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setAddPhoneDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAddPhone} 
                disabled={savingPhone}
                className="bg-customer hover:bg-customer/90 text-customer-foreground"
              >
                {savingPhone ? "Saving..." : "Add Phone"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Results Section */}
        {step === "gifts" && (
          <section className="container py-4">
            {isLoading && (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-customer" />
              </div>
            )}

            {error && !isLoading && (
              <div className="max-w-4xl mx-auto mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0" />
                <p className="text-destructive">{error}</p>
              </div>
            )}

            {!isLoading && gifts.length === 0 && !error && (
              <div className="max-w-md mx-auto text-center py-12">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                  <ShoppingBag className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">No purchases found</h3>
                <p className="text-muted-foreground">
                  No group purchases found for this {verifiedEmail ? "email" : "phone"}.
                </p>
              </div>
            )}

            {!isLoading && gifts.length >= 0 && !error && (
              <div className="max-w-6xl mx-auto space-y-4">
                {/* Row 1: Upcoming Events (2 cols) + Start Something New (1 col) */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {/* Upcoming Events - spans 2 columns */}
                  <div className="md:col-span-2">
                    <CustomerImportantDates 
                      customerEmail={verifiedEmail} 
                      customerPhone={verifiedPhone}
                      compact
                      dialogOpenProp={openAddDateDialog}
                      onDialogClose={() => setOpenAddDateDialog(false)}
                      preselectedDate={calendarSelectedDate}
                      onAddNew={(selectedDate) => {
                        setCalendarSelectedDate(selectedDate);
                        setOpenEventTypePicker(true);
                      }}
                    />
                  </div>

                  {/* Start Something New - Engaging CTA Card */}
                  <Card className="overflow-hidden h-full bg-gradient-to-br from-pink-500/10 via-violet-500/10 to-emerald-500/10 border-0 shadow-lg">
                    <CardContent className="p-0 h-full flex flex-col relative">
                      {/* Decorative background elements */}
                      <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-pink-400/20 to-transparent rounded-full blur-2xl" />
                      <div className="absolute bottom-0 left-0 w-20 h-20 bg-gradient-to-tr from-emerald-400/20 to-transparent rounded-full blur-2xl" />
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-violet-400/10 rounded-full blur-xl" />
                      
                      {/* Header with sparkle animation */}
                      <div className="bg-gradient-to-r from-pink-500 via-violet-500 to-emerald-500 px-3 py-2">
                        <div className="flex items-center gap-2 text-white">
                          <Sparkles className="w-4 h-4 animate-pulse" />
                          <h2 className="font-bold text-sm">Start Something New</h2>
                        </div>
                        <p className="text-white/80 text-xs">Bring people together ✨</p>
                      </div>
                      
                      <div className="p-3 flex-1 flex flex-col justify-center space-y-2 relative z-10">
                        <Button 
                          variant="outline" 
                          className="w-full justify-start gap-2 h-auto py-2 bg-white/80 dark:bg-background/80 backdrop-blur-sm border-pink-300 hover:bg-pink-50 dark:hover:bg-pink-950/30 hover:border-pink-400 hover:scale-[1.02] transition-all shadow-sm"
                          onClick={() => setOpenGroupGiftDialog(true)}
                        >
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center shadow-md">
                            <Gift className="w-4 h-4 text-white" />
                          </div>
                          <div className="text-left flex-1">
                            <p className="font-semibold text-foreground text-sm">Start a Group Gift</p>
                            <p className="text-xs text-muted-foreground">Plan a gift with friends</p>
                          </div>
                          <Heart className="w-3 h-3 text-pink-400" />
                        </Button>
                        
                        <Button 
                          variant="outline" 
                          className="w-full justify-start gap-2 h-auto py-2 bg-white/80 dark:bg-background/80 backdrop-blur-sm border-violet-300 hover:bg-violet-50 dark:hover:bg-violet-950/30 hover:border-violet-400 hover:scale-[1.02] transition-all shadow-sm"
                          onClick={() => setOpenGroupPurchaseDialog(true)}
                        >
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-400 to-violet-600 flex items-center justify-center shadow-md">
                            <ShoppingBag className="w-4 h-4 text-white" />
                          </div>
                          <div className="text-left flex-1">
                            <p className="font-semibold text-foreground text-sm">Make a Shared Purchase</p>
                            <p className="text-xs text-muted-foreground">Split the cost together</p>
                          </div>
                          <Users className="w-3 h-3 text-violet-400" />
                        </Button>
                        
                        <Button 
                          variant="outline" 
                          className="w-full justify-start gap-2 h-auto py-2 bg-white/80 dark:bg-background/80 backdrop-blur-sm border-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 hover:border-emerald-400 hover:scale-[1.02] transition-all shadow-sm"
                          onClick={() => setOpenGroupPaymentDialog(true)}
                        >
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-md">
                            <CreditCard className="w-4 h-4 text-white" />
                          </div>
                          <div className="text-left flex-1">
                            <p className="font-semibold text-foreground text-sm">Split a Payment</p>
                            <p className="text-xs text-muted-foreground">Collect for trips & events</p>
                          </div>
                          <Wallet className="w-3 h-3 text-emerald-400" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Row 2: Calendar, Stores, Journey */}
                <CustomerEngagementSection 
                  customerEmail={verifiedEmail} 
                  customerPhone={verifiedPhone}
                  gifts={gifts}
                />

                {gifts.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <h2 className="text-lg font-semibold text-foreground">Your Split Orders</h2>
                        <p className="text-sm text-muted-foreground">Track every split purchase and the store it belongs to.</p>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {gifts.length} order{gifts.length === 1 ? "" : "s"}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                      {gifts.map((gift) => {
                        const requestCurrency = (gift.request.currency as Currency | null) || "USD";

                        return (
                          <Card key={gift.id} className="border-border/60 shadow-sm">
                            <CardContent className="p-4">
                              <div className="flex gap-4">
                                <div className="w-20 h-20 rounded-xl overflow-hidden bg-muted flex items-center justify-center shrink-0">
                                  {gift.request.product_image_url ? (
                                    <img
                                      src={gift.request.product_image_url}
                                      alt={gift.request.product_name}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <ShoppingBag className="w-8 h-8 text-muted-foreground" />
                                  )}
                                </div>

                                <div className="flex-1 min-w-0 space-y-3">
                                  <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <p className="text-sm text-muted-foreground">
                                        {gift.request.store_name || "Store unavailable"}
                                      </p>
                                      <h3 className="font-semibold text-foreground line-clamp-2">
                                        {gift.request.product_name}
                                      </h3>
                                      <p className="text-xs text-muted-foreground mt-1">
                                        Started {format(new Date(gift.request.created_at), "dd MMM yyyy")}
                                      </p>
                                    </div>
                                    {getStatusBadge(gift.request.status, gift.has_paid)}
                                  </div>

                                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                                    <div>
                                      <p className="text-muted-foreground text-xs">Order Total</p>
                                      <p className="font-semibold">
                                        {formatCurrency(gift.request.total_price, requestCurrency)}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-muted-foreground text-xs">Your Share</p>
                                      <p className="font-semibold">
                                        {formatCurrency(gift.request.split_amount, requestCurrency)}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-muted-foreground text-xs">Payment Progress</p>
                                      <p className="font-semibold">
                                        {gift.request.paid_count}/{gift.request.participant_count} paid
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-muted-foreground text-xs">Deadline</p>
                                      <p className="font-semibold">
                                        {format(new Date(gift.request.deadline), "dd MMM")}
                                      </p>
                                    </div>
                                  </div>

                                  <div className="flex flex-wrap items-center gap-2 text-xs">
                                    <Badge variant="secondary" className="font-normal">
                                      {gift.is_organizer ? "Organizer" : "Participant"}
                                    </Badge>
                                    {gift.request.shopify_order_id && (
                                      <Badge variant="outline" className="font-normal">
                                        Store Order #{gift.request.shopify_order_id}
                                      </Badge>
                                    )}
                                    <span className="text-muted-foreground">{getFulfillmentLabel(gift)}</span>
                                  </div>

                                  <div className="flex flex-wrap gap-2">
                                    <Button asChild size="sm">
                                      <Link to={`/split/${gift.request.id}?email=${encodeURIComponent(gift.email)}`}>
                                        Track Order
                                      </Link>
                                    </Button>
                                    {gift.request.store_domain && (
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => window.open(`https://${gift.request.store_domain}`, "_blank", "noopener,noreferrer")}
                                      >
                                        Visit Store
                                      </Button>
                                    )}
                                  </div>

                                  {gift.is_organizer && gift.request.participants.some((participant) => !participant.is_organizer && !participant.has_paid) && (
                                    <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
                                      <div className="flex items-center justify-between gap-2">
                                        <p className="text-sm font-medium">Participant payment links</p>
                                        <Badge variant="outline" className="text-xs">
                                          {gift.request.participants.filter((participant) => !participant.is_organizer && !participant.has_paid).length} pending
                                        </Badge>
                                      </div>
                                      <div className="space-y-2">
                                        {gift.request.participants
                                          .filter((participant) => !participant.is_organizer && !participant.has_paid)
                                          .map((participant) => (
                                            <div key={participant.id} className="flex flex-col gap-2 rounded-md bg-background p-2 sm:flex-row sm:items-center sm:justify-between">
                                              <div className="min-w-0">
                                                <p className="truncate text-sm font-medium">{participant.name || "Participant"}</p>
                                                <p className="truncate text-xs text-muted-foreground">{participant.email}</p>
                                              </div>
                                              <div className="flex flex-wrap gap-2">
                                                <Button
                                                  type="button"
                                                  variant="outline"
                                                  size="sm"
                                                  onClick={() => handleCopyParticipantPaymentLink(gift.request.id, participant.email)}
                                                >
                                                  <Copy className="mr-1 h-3 w-3" />
                                                  Copy
                                                </Button>
                                                <Button
                                                  type="button"
                                                  variant="outline"
                                                  size="sm"
                                                  onClick={() => handleShareParticipantPaymentLink(gift.request.id, participant)}
                                                >
                                                  <Share2 className="mr-1 h-3 w-3" />
                                                  Share
                                                </Button>
                                                <Button
                                                  type="button"
                                                  size="sm"
                                                  onClick={() => handleResendParticipantPaymentLink(gift.request.id, participant.id)}
                                                  disabled={resendingParticipantId === participant.id}
                                                >
                                                  {resendingParticipantId === participant.id ? (
                                                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                                  ) : (
                                                    <Mail className="mr-1 h-3 w-3" />
                                                  )}
                                                  Email
                                                </Button>
                                              </div>
                                            </div>
                                          ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Row 3: View All Activity Link */}
                <div className="flex justify-center">
                  <Link 
                    to="/my-activity" 
                    className="text-sm text-white font-medium transition-all flex items-center gap-1.5 px-6 py-3 rounded-full bg-gradient-to-r from-pink-500 via-violet-500 to-emerald-500 hover:from-pink-600 hover:via-violet-600 hover:to-emerald-600 shadow-lg hover:shadow-xl hover:scale-105"
                  >
                    <Clock className="w-4 h-4" />
                    View All Activity & Reports
                    <ExternalLink className="w-3 h-3" />
                  </Link>
                </div>

                {/* Social Share Section - Now at bottom */}
                <SocialShareSection />
              </div>
            )}
          </section>
        )}

        {/* How It Works */}
        {step === "email" && (
          <section className="container py-12">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-lg font-semibold text-center text-foreground mb-8">How It Works</h2>
              <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12">
                <div className="flex items-center gap-3 text-center">
                  <div className="w-10 h-10 rounded-full bg-customer/10 flex items-center justify-center">
                    <span className="text-lg">🛍️</span>
                  </div>
                  <span className="text-muted-foreground">Get Invited</span>
                </div>
                <span className="hidden md:block text-muted-foreground text-2xl">→</span>
                <div className="flex items-center gap-3 text-center">
                  <div className="w-10 h-10 rounded-full bg-customer/10 flex items-center justify-center">
                    <span className="text-lg">💳</span>
                  </div>
                  <span className="text-muted-foreground">Pay Your Share</span>
                </div>
                <span className="hidden md:block text-muted-foreground text-2xl">→</span>
                <div className="flex items-center gap-3 text-center">
                  <div className="w-10 h-10 rounded-full bg-customer/10 flex items-center justify-center">
                    <span className="text-lg">📦</span>
                  </div>
                  <span className="text-muted-foreground">Order Ships</span>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Share Section */}
        {step === "email" && (
          <section className="container py-4">
            <div className="bg-gradient-to-br from-customer/5 to-purple-100/30 dark:from-customer/10 dark:to-purple-950/20 rounded-xl p-4 text-center max-w-md mx-auto border border-customer/10">
              <p className="text-muted-foreground text-sm mb-3">
                Know a store that should offer Shakatah? 💜
              </p>
              <RecommendStoreDialog>
                <Button 
                  variant="outline"
                  size="sm"
                  className="border-customer/30 text-customer hover:bg-customer/10"
                >
                  <Store className="mr-2 w-4 h-4" />
                  Recommend a Store
                </Button>
              </RecommendStoreDialog>
            </div>
          </section>
        )}
      </main>
      
      <footer className="fixed bottom-0 left-0 right-0 z-40 border-t border-border/50 bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/90">
        <div className="container py-2">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <Gift className="w-4 h-4 text-customer" />
              <span className="font-semibold text-foreground">Shakatah</span>
            </div>
            <div className="flex items-center gap-3 flex-wrap justify-center">
              <RecommendFriendDialog defaultEmail={verifiedEmail}>
                <button className="flex items-center gap-1 hover:text-customer transition-colors">
                  <Users className="w-3 h-3" />
                  Recommend to Friend
                </button>
              </RecommendFriendDialog>
              <CustomerFeedbackDialog defaultEmail={verifiedEmail}>
                <button className="flex items-center gap-1 hover:text-customer transition-colors">
                  <MessageSquare className="w-3 h-3" />
                  Feedback
                </button>
              </CustomerFeedbackDialog>
              <span className="text-border">|</span>
              <Link to="/terms-of-service" className="hover:text-foreground transition-colors">Terms</Link>
              <Link to="/privacy-policy" className="hover:text-foreground transition-colors">Privacy</Link>
              <Link to="/refund-policy" className="hover:text-foreground transition-colors">Refunds</Link>
              <a href="/#faq" className="hover:text-foreground transition-colors">FAQ</a>
              <Link to="/contact" className="hover:text-foreground transition-colors">Contact</Link>
            </div>
          </div>
        </div>
      </footer>
      
      <FAQChatBot context="customer" />
      
      <AddGroupPurchaseDialog
        open={openGroupPurchaseDialog}
        onOpenChange={setOpenGroupPurchaseDialog}
        organizerEmail={verifiedEmail || ""}
        organizerName={undefined}
      />
      
      <AddGroupPaymentDialog
        open={openGroupPaymentDialog}
        onOpenChange={setOpenGroupPaymentDialog}
        organizerEmail={verifiedEmail || ""}
        organizerName={undefined}
      />
      
      <AddGroupGiftDialog
        open={openGroupGiftDialog}
        onOpenChange={setOpenGroupGiftDialog}
        organizerEmail={verifiedEmail || ""}
        organizerName={undefined}
        preselectedDate={calendarSelectedDate}
      />

      {/* Event Type Picker Dialog */}
      <Dialog open={openEventTypePicker} onOpenChange={setOpenEventTypePicker}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>What would you like to create?</DialogTitle>
            <DialogDescription>
              {calendarSelectedDate 
                ? `For ${format(calendarSelectedDate, "MMMM d, yyyy")} — choose the type of event.`
                : "Choose the type of event to get started."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Button
              variant="outline"
              className="w-full justify-start gap-3 h-auto py-3"
              onClick={() => { setOpenEventTypePicker(false); setOpenGroupGiftDialog(true); }}
            >
              <div className="w-8 h-8 rounded-full bg-pink-100 dark:bg-pink-950/40 flex items-center justify-center">
                <Gift className="w-4 h-4 text-pink-600 dark:text-pink-400" />
              </div>
              <div className="text-left">
                <p className="font-medium text-sm">Group Gift</p>
                <p className="text-xs text-muted-foreground">Plan a gift with friends</p>
              </div>
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start gap-3 h-auto py-3"
              onClick={() => { setOpenEventTypePicker(false); setOpenGroupPurchaseDialog(true); }}
            >
              <div className="w-8 h-8 rounded-full bg-violet-100 dark:bg-violet-950/40 flex items-center justify-center">
                <ShoppingBag className="w-4 h-4 text-violet-600 dark:text-violet-400" />
              </div>
              <div className="text-left">
                <p className="font-medium text-sm">Group Purchase</p>
                <p className="text-xs text-muted-foreground">Split the cost of an item</p>
              </div>
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start gap-3 h-auto py-3"
              onClick={() => { setOpenEventTypePicker(false); setOpenGroupPaymentDialog(true); }}
            >
              <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-950/40 flex items-center justify-center">
                <CreditCard className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="text-left">
                <p className="font-medium text-sm">Collect Payment</p>
                <p className="text-xs text-muted-foreground">Collect for trips, events & more</p>
              </div>
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start gap-3 h-auto py-3"
              onClick={() => { setOpenEventTypePicker(false); setOpenAddDateDialog(true); }}
            >
              <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-950/40 flex items-center justify-center">
                <Bell className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="text-left">
                <p className="font-medium text-sm">Set a Reminder</p>
                <p className="text-xs text-muted-foreground">Remember birthdays & events</p>
              </div>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyGifts;
